import { Badge } from "@/components/ui/badge.tsx";
import { Note } from "@/components/ui/note.tsx";
import { LockClosedIcon } from "@heroicons/react/20/solid";
import { useI18nextInstance } from "@squide/i18next";
import { useTranslation } from "react-i18next";
import { segmentsI18NextInstanceKey } from "../../i18n/i18next.ts";

export interface RequiredRuleRowProps {
    isOwnedByViewer: boolean;
    ownerDisplayName?: string;
    canAddRules: boolean;
    layout: "page" | "compact";
}

export function RequiredRuleRow({ isOwnedByViewer, ownerDisplayName, canAddRules, layout }: RequiredRuleRowProps) {
    const i18nextInstance = useI18nextInstance(segmentsI18NextInstanceKey);
    const { t } = useTranslation("segmentRules", { i18n: i18nextInstance });

    const label = isOwnedByViewer ? t("requiredRule.ownLabel") : t("requiredRule.otherLabel");
    const title = isOwnedByViewer ? t("requiredRule.ownTitle") : ownerDisplayName ? t("requiredRule.otherTitle", { name: ownerDisplayName }) : t("requiredRule.otherTitleFallback");
    const description = isOwnedByViewer ? t("requiredRule.ownDescription") : t("requiredRule.otherDescription");

    const content = (
        <>
            <div className="flex flex-wrap items-center gap-2">
                <Badge intent="primary" className="tracking-wide uppercase">
                    <LockClosedIcon aria-hidden="true" />
                    {label}
                </Badge>
                <span className="text-fg text-sm font-medium">{title}</span>
            </div>
            <p className="text-muted-fg text-sm">{description}</p>
            {canAddRules && isOwnedByViewer && <p className="text-muted-fg text-sm">{t("requiredRule.ownHint")}</p>}
        </>
    );

    if (layout === "compact") {
        return (
            <Note indicator={false} className="flex flex-col gap-1">
                {content}
            </Note>
        );
    }

    return <div className="bg-primary-subtle/50 border-border -mx-(--gutter) -mt-(--gutter) flex flex-col gap-1 rounded-t-lg border-b px-(--gutter) pt-4 pb-3">{content}</div>;
}
